import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../services/api";
import {
  createCampaign as apiCreateCampaign,
  createEmailTemplate as apiCreateEmailTemplate,
  createSmsTemplate as apiCreateSmsTemplate,
  deleteCampaign as apiDeleteCampaign,
  deleteEmailTemplate as apiDeleteEmailTemplate,
  deleteSmsTemplate as apiDeleteSmsTemplate,
  deriveUseAllCustomers,
  fetchCampaigns,
  fetchEmailTemplates,
  fetchSmsTemplates,
  mapCampaignFromApi,
  updateCampaignApi,
  updateEmailTemplate as apiUpdateEmailTemplate,
  updateSmsTemplate as apiUpdateSmsTemplate,
} from "../services/marketingTemplatesApi";
import type { CampaignApiRow } from "../services/marketingTemplatesApi";

export type EmailTemplate = {
  id: string;
  name: string;
  description?: string;
  html: string;
};

export type SMSTemplate = {
  id: string;
  name: string;
  text: string;
  charLimit?: number;
};

export type MarketingCustomer = {
  id: string;
  name: string;
  email?: string;
  mobile?: string;
};

export type Campaign = {
  id: string;
  name: string;
  description: string;
  selectedCustomerIds: string[];
  useAllCustomers: boolean;
  emailTemplateId?: string | null;
  smsTemplateId?: string | null;
  creditsUsed: number;
};

type CalculateCreditsArgs = {
  selectedCustomerIds?: string[];
  useAllCustomers?: boolean;
  emailTemplateId?: string | null;
  smsTemplateId?: string | null;
};

type MarketingContextValue = {
  emailTemplates: EmailTemplate[];
  smsTemplates: SMSTemplate[];
  campaigns: Campaign[];
  customers: MarketingCustomer[];
  customersLoading: boolean;
  emailTemplatesLoading: boolean;
  smsTemplatesLoading: boolean;
  campaignsLoading: boolean;
  refreshEmailAndSmsTemplates: () => Promise<void>;
  refreshCampaigns: () => Promise<void>;
  refreshCreditBalanceFromStorage: () => void;
  startingCredits: number;
  remainingCredits: number;
  calculateCreditsNeeded: (args: CalculateCreditsArgs) => number;
  addEmailTemplate: (template: Omit<EmailTemplate, "id">) => Promise<EmailTemplate>;
  updateEmailTemplate: (id: string, patch: Partial<Omit<EmailTemplate, "id">>) => Promise<EmailTemplate>;
  addSMSTemplate: (template: Omit<SMSTemplate, "id" | "charLimit"> & { charLimit?: number }) => Promise<SMSTemplate>;
  updateSMSTemplate: (
    id: string,
    patch: Partial<Omit<SMSTemplate, "id">>,
  ) => Promise<SMSTemplate>;
  removeEmailTemplate: (id: string) => Promise<void>;
  removeSMSTemplate: (id: string) => Promise<void>;
  addCampaign: (campaign: Omit<Campaign, "id" | "creditsUsed">) => Promise<void>;
  updateCampaign: (id: string, patch: Omit<Campaign, "id" | "creditsUsed">) => Promise<void>;
  removeCampaign: (id: string) => Promise<void>;
};

const MarketingContext = createContext<MarketingContextValue | undefined>(undefined);

const EMAIL_COST_PER_RECIPIENT = 0.03;
const SMS_COST_PER_RECIPIENT = 0.1;
const FALLBACK_STARTING_CREDITS = 30;

function getStoredCreditBalance(): number {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return FALLBACK_STARTING_CREDITS;
    const parsed = JSON.parse(raw) as {
      company?: { credit_balance?: number | string | null };
      credit_balance?: number | string | null;
    };
    const credit = parsed?.company?.credit_balance ?? parsed?.credit_balance;
    const num = Number(credit);
    return Number.isFinite(num) ? num : FALLBACK_STARTING_CREDITS;
  } catch {
    return FALLBACK_STARTING_CREDITS;
  }
}

export const MarketingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [smsTemplates, setSmsTemplates] = useState<SMSTemplate[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [customers, setCustomers] = useState<MarketingCustomer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);
  const [smsTemplatesLoading, setSmsTemplatesLoading] = useState(false);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [startingCredits, setStartingCredits] = useState<number>(() => getStoredCreditBalance());

  const refreshCreditBalanceFromStorage = useCallback(() => {
    setStartingCredits(getStoredCreditBalance());
  }, []);

  useEffect(() => {
    refreshCreditBalanceFromStorage();
    window.addEventListener("storage", refreshCreditBalanceFromStorage);
    window.addEventListener("focus", refreshCreditBalanceFromStorage);
    return () => {
      window.removeEventListener("storage", refreshCreditBalanceFromStorage);
      window.removeEventListener("focus", refreshCreditBalanceFromStorage);
    };
  }, [refreshCreditBalanceFromStorage]);

  useEffect(() => {
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const response = await api.get("/admin/customers?per_page=1000&page=1");
        const payload = response.data;
        const possibleCollections = [
          payload,
          payload?.data,
          payload?.data?.data,
          payload?.data?.data?.data,
        ];

        let rawCustomers: any[] = [];
        for (const collection of possibleCollections) {
          if (Array.isArray(collection)) {
            rawCustomers = collection;
            break;
          }
        }

        const mapped = rawCustomers
          .map((customer: any) => ({
            id: String(customer.id),
            name: customer.name || "Unnamed Customer",
            email: customer.email || undefined,
            mobile: customer.mobile || customer.phone || undefined,
          }))
          .filter((customer) => customer.id);

        setCustomers(mapped);
      } catch {
        setCustomers([]);
      } finally {
        setCustomersLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  const refreshEmailAndSmsTemplates = useCallback(async () => {
    setEmailTemplatesLoading(true);
    setSmsTemplatesLoading(true);
    try {
      const [emails, sms] = await Promise.all([fetchEmailTemplates(), fetchSmsTemplates()]);
      setEmailTemplates(emails);
      setSmsTemplates(sms);
    } catch {
      setEmailTemplates([]);
      setSmsTemplates([]);
    } finally {
      setEmailTemplatesLoading(false);
      setSmsTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshEmailAndSmsTemplates();
  }, [refreshEmailAndSmsTemplates]);

  const calculateCreditsNeeded = useCallback(
    ({
      selectedCustomerIds = [],
      useAllCustomers = false,
      emailTemplateId,
      smsTemplateId,
    }: CalculateCreditsArgs) => {
      const recipientsCount = useAllCustomers
        ? customers.length
        : selectedCustomerIds.length;

      if (!recipientsCount) return 0;

      const perRecipient =
        (emailTemplateId ? EMAIL_COST_PER_RECIPIENT : 0) +
        (smsTemplateId ? SMS_COST_PER_RECIPIENT : 0);
      return recipientsCount * perRecipient;
    },
    [customers],
  );

  const buildCampaignFromRow = useCallback(
    (row: CampaignApiRow): Campaign => {
      const core = mapCampaignFromApi(row, customers);
      const calculatedCredits = calculateCreditsNeeded({
        selectedCustomerIds: core.selectedCustomerIds,
        useAllCustomers: core.useAllCustomers,
        emailTemplateId: core.emailTemplateId,
        smsTemplateId: core.smsTemplateId,
      });
      const storedCredits = Number(row.credit_used);
      return {
        ...core,
        creditsUsed: Number.isFinite(storedCredits) ? storedCredits : calculatedCredits,
      };
    },
    [customers, calculateCreditsNeeded],
  );

  const refreshCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const rows = await fetchCampaigns();
      setCampaigns(rows.map((row) => buildCampaignFromRow(row)));
    } catch {
      setCampaigns([]);
    } finally {
      setCampaignsLoading(false);
    }
  }, [buildCampaignFromRow]);

  useEffect(() => {
    void refreshCampaigns();
  }, [refreshCampaigns]);

  useEffect(() => {
    setCampaigns((prev) =>
      prev.map((c) => {
        const useAll = deriveUseAllCustomers(c.selectedCustomerIds, customers);
        return {
          ...c,
          useAllCustomers: useAll,
          creditsUsed: calculateCreditsNeeded({
            selectedCustomerIds: c.selectedCustomerIds,
            useAllCustomers: useAll,
            emailTemplateId: c.emailTemplateId,
            smsTemplateId: c.smsTemplateId,
          }),
        };
      }),
    );
  }, [customers, calculateCreditsNeeded]);

  // For now we show the raw company credit balance exactly as stored.
  const remainingCredits = useMemo(() => startingCredits, [startingCredits]);

  const addEmailTemplate: MarketingContextValue["addEmailTemplate"] = async (template) => {
    const created = await apiCreateEmailTemplate({
      name: template.name,
      description: template.description ?? "",
      html_content: template.html,
    });
    setEmailTemplates((prev) => [...prev, created]);
    return created;
  };

  const updateEmailTemplate: MarketingContextValue["updateEmailTemplate"] = async (id, patch) => {
    const payload: Partial<{ name: string; description: string; html_content: string }> = {};
    if (patch.name !== undefined) payload.name = patch.name;
    if (patch.description !== undefined) payload.description = patch.description;
    if (patch.html !== undefined) payload.html_content = patch.html;
    const updated = await apiUpdateEmailTemplate(id, payload);
    setEmailTemplates((prev) => prev.map((t) => (t.id === id ? updated : t)));
    return updated;
  };

  const addSMSTemplate: MarketingContextValue["addSMSTemplate"] = async (template) => {
    const charLimit = template.charLimit ?? 160;
    const created = await apiCreateSmsTemplate({
      name: template.name,
      text_content: template.text,
      char_limit: charLimit,
    });
    setSmsTemplates((prev) => [...prev, created]);
    return created;
  };

  const updateSMSTemplate: MarketingContextValue["updateSMSTemplate"] = async (id, patch) => {
    const payload: Partial<{ name: string; text_content: string; char_limit: number }> = {};
    if (patch.name !== undefined) payload.name = patch.name;
    if (patch.text !== undefined) payload.text_content = patch.text;
    if (patch.charLimit !== undefined) payload.char_limit = patch.charLimit;
    const updated = await apiUpdateSmsTemplate(id, payload);
    setSmsTemplates((prev) => prev.map((t) => (t.id === id ? updated : t)));
    return updated;
  };

  const removeEmailTemplate: MarketingContextValue["removeEmailTemplate"] = async (id) => {
    await apiDeleteEmailTemplate(id);
    setEmailTemplates((prev) => prev.filter((t) => t.id !== id));
    await refreshCampaigns();
  };

  const removeSMSTemplate: MarketingContextValue["removeSMSTemplate"] = async (id) => {
    await apiDeleteSmsTemplate(id);
    setSmsTemplates((prev) => prev.filter((t) => t.id !== id));
    await refreshCampaigns();
  };

  const addCampaign: MarketingContextValue["addCampaign"] = async (campaignInput) => {
    const customer_ids = (
      campaignInput.useAllCustomers
        ? customers.map((c) => c.id)
        : campaignInput.selectedCustomerIds
    ).map((cid) => Number(cid));
    const credit_used = calculateCreditsNeeded({
      selectedCustomerIds: campaignInput.selectedCustomerIds,
      useAllCustomers: campaignInput.useAllCustomers,
      emailTemplateId: campaignInput.emailTemplateId,
      smsTemplateId: campaignInput.smsTemplateId,
    });
    const payload: {
      name: string;
      description: string;
      customer_ids: number[];
      credit_used: number;
      email_template_id?: number;
      sms_template_id?: number;
    } = {
      name: campaignInput.name,
      description: campaignInput.description,
      customer_ids,
      credit_used,
    };
    if (campaignInput.emailTemplateId) payload.email_template_id = Number(campaignInput.emailTemplateId);
    if (campaignInput.smsTemplateId) payload.sms_template_id = Number(campaignInput.smsTemplateId);
    await apiCreateCampaign(payload);
    await refreshCampaigns();
  };

  const updateCampaign: MarketingContextValue["updateCampaign"] = async (id, patch) => {
    const customer_ids = (
      patch.useAllCustomers ? customers.map((c) => c.id) : patch.selectedCustomerIds
    ).map((cid) => Number(cid));
    const credit_used = calculateCreditsNeeded({
      selectedCustomerIds: patch.selectedCustomerIds,
      useAllCustomers: patch.useAllCustomers,
      emailTemplateId: patch.emailTemplateId,
      smsTemplateId: patch.smsTemplateId,
    });
    const payload: {
      name: string;
      description: string;
      customer_ids: number[];
      credit_used: number;
      email_template_id?: number;
      sms_template_id?: number;
    } = {
      name: patch.name,
      description: patch.description,
      customer_ids,
      credit_used,
    };
    if (patch.emailTemplateId) payload.email_template_id = Number(patch.emailTemplateId);
    if (patch.smsTemplateId) payload.sms_template_id = Number(patch.smsTemplateId);
    await updateCampaignApi(id, payload);
    await refreshCampaigns();
  };

  const removeCampaign: MarketingContextValue["removeCampaign"] = async (id) => {
    await apiDeleteCampaign(id);
    await refreshCampaigns();
  };

  const value: MarketingContextValue = {
    emailTemplates,
    smsTemplates,
    campaigns,
    customers,
    customersLoading,
    emailTemplatesLoading,
    smsTemplatesLoading,
    campaignsLoading,
    refreshEmailAndSmsTemplates,
    refreshCampaigns,
    refreshCreditBalanceFromStorage,
    startingCredits,
    remainingCredits,
    calculateCreditsNeeded,
    addEmailTemplate,
    updateEmailTemplate,
    addSMSTemplate,
    updateSMSTemplate,
    removeEmailTemplate,
    removeSMSTemplate,
    addCampaign,
    updateCampaign,
    removeCampaign,
  };

  return <MarketingContext.Provider value={value}>{children}</MarketingContext.Provider>;
};

export const useMarketing = () => {
  const ctx = useContext(MarketingContext);
  if (!ctx) {
    throw new Error("useMarketing must be used within a MarketingProvider");
  }
  return ctx;
};
